import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Bell, HelpCircle, Plus, ChevronDown, Settings, LogOut, User, CalendarDays, Users, BookOpen, FileText, Receipt, Briefcase, FileCheck, GraduationCap, CreditCard, Crown, ArrowLeftRight } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import api, { API_BASE, getToken, setToken } from '../../api/client';
import GlobalSearch from '../GlobalSearch';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';

const QUICK_CREATE = [
  { label: 'Session', icon: CalendarDays, route: '/app/sessions' },
  { label: 'Apprenant', icon: Users, route: '/app/stagiaires' },
  { label: 'Programme', icon: BookOpen, route: '/app/programmes' },
  { label: 'Devis', icon: FileText, route: '/app/devis' },
  { label: 'Facture', icon: Receipt, route: '/app/factures' },
  { label: 'Opportunité', icon: Briefcase, route: '/app/crm' },
];

export default function Topbar({ title }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [pulseBadge, setPulseBadge] = useState(false);
  const [bellOpen, setBellOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [unreadOnly, setUnreadOnly] = useState(false);  // Vague 99.3 — bell filter
  const [org, setOrg] = useState(null);
  const seenIdsRef = useRef(new Set());

  useEffect(() => {
    api.get('/notifications?limit=20').then(({ data }) => {
      const items = (data?.items || data || []).map((n) => ({ ...n, unread: n.read !== true }));
      setNotifications(items);
      items.forEach((n) => n.id && seenIdsRef.current.add(n.id));
    }).catch(() => {});
    api.get('/orgs/me').then(({ data }) => setOrg(data)).catch(() => {});
  }, []);

  // Live SSE stream — prepend new notifications, pulse the bell badge.
  useEffect(() => {
    const token = getToken();
    if (!token || typeof window === 'undefined') return;
    const url = `${API_BASE}/notifications/stream?token=${encodeURIComponent(token)}`;
    let es;
    try { es = new EventSource(url); } catch { return; }

    const onNotification = (ev) => {
      let n;
      try { n = JSON.parse(ev.data); } catch { return; }
      if (!n || !n.id || seenIdsRef.current.has(n.id)) return;
      seenIdsRef.current.add(n.id);
      // Skip backlog older than 60s
      const created = n.created_at ? new Date(n.created_at) : null;
      if (created && Date.now() - created.getTime() > 60_000) return;
      setNotifications((prev) => [{ ...n, unread: n.read !== true }, ...prev].slice(0, 50));
      setPulseBadge(true);
      // Stop pulsing after 3s
      setTimeout(() => setPulseBadge(false), 3000);
    };
    es.addEventListener('notification', onNotification);
    return () => {
      try { es.removeEventListener('notification', onNotification); } catch (_) { /* noop */ }
      try { es.close(); } catch (_) { /* noop */ }
    };
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setSearchOpen(true);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const unread = notifications.filter((n) => n.unread).length;

  // Auto mark-all-as-read when the bell popover stays open for 1.5s (Gmail/Slack pattern)
  useEffect(() => {
    if (!bellOpen || unread === 0) return;
    const t = setTimeout(() => {
      setNotifications((prev) => prev.map((n) => ({ ...n, unread: false })));
      setPulseBadge(false);
      api.post('/notifications/read-all').catch(() => { /* optimistic */ });
    }, 1500);
    return () => clearTimeout(t);
  }, [bellOpen, unread]);

  const markAll = async () => {
    setNotifications((prev) => prev.map((n) => ({ ...n, unread: false })));
    setPulseBadge(false);
    try { await api.post('/notifications/read-all'); } catch { /* optimistic */ }
  };

  const markOne = async (id) => {
    setNotifications((prev) => prev.map((n) => n.id === id ? { ...n, unread: false } : n));
    try { await api.post(`/notifications/${id}/read`); } catch { /* optimistic */ }
  };

  const formatRelative = (iso) => {
    if (!iso) return '';
    const d = typeof iso === 'string' ? new Date(iso) : iso;
    if (Number.isNaN(d.getTime())) return '';
    const diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 60) return 'à l\u2019instant';
    if (diff < 3600) return `il y a ${Math.floor(diff / 60)} min`;
    if (diff < 86400) return `il y a ${Math.floor(diff / 3600)} h`;
    return `il y a ${Math.floor(diff / 86400)} j`;
  };

  const EVENT_META = {
    'quote.accepted':         { Icon: FileCheck,      tint: 'bg-[#E6FBF7] text-[#0F766E]' },
    'invoice.paid':           { Icon: CreditCard,     tint: 'bg-[#E6FBF7] text-[#0F766E]' },
    'invoice.validated':      { Icon: FileText,       tint: 'bg-[#EEF2FF] text-[#4F46E5]' },
    'learner.completed':      { Icon: GraduationCap,  tint: 'bg-[#E0F2FE] text-[#0369A1]' },
    'questionnaire.submitted':{ Icon: FileCheck,      tint: 'bg-[#E0F2FE] text-[#0369A1]' },
    'companies.created':      { Icon: Briefcase,      tint: 'bg-[#FEF3E7] text-[#B45309]' },
    'learners.created':       { Icon: Users,          tint: 'bg-[#FEF3E7] text-[#B45309]' },
    'sessions.created':       { Icon: CalendarDays,   tint: 'bg-[#EEF2FF] text-[#4F46E5]' },
    'programs.created':       { Icon: BookOpen,       tint: 'bg-[#EEF2FF] text-[#4F46E5]' },
    'trainers.created':       { Icon: GraduationCap,  tint: 'bg-[#FEF3E7] text-[#B45309]' },
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login', { replace: true });
  };

  // Vague 97.6 Phase 4 — impersonation banner state
  const [impersonation, setImpersonation] = React.useState(null);
  React.useEffect(() => {
    try {
      const raw = sessionStorage.getItem('ifaap_impersonation');
      if (raw) setImpersonation(JSON.parse(raw));
    } catch (e) { console.warn("[silent]", e); }
  }, []);

  const stopImpersonation = async () => {
    try { await api.post('/auth/impersonate-stop'); } catch (e) { console.warn("[silent]", e); }
    try { sessionStorage.removeItem('ifaap_impersonation'); } catch (e) { console.warn("[silent]", e); }
    // Clear session token; the user will be redirected to /login by AuthContext.
    try {
      sessionStorage.removeItem('ifaap_session_token');
      localStorage.removeItem('ifaap_session_token');
    } catch (e) { console.warn("[silent]", e); }
    window.location.href = '/login';
  };

  const displayName = user?.name || 'Utilisateur';
  // Vague 114 — multi-org switcher: list every org attached to this email.
  const [myOrgs, setMyOrgs] = useState([]);
  useEffect(() => {
    api.get('/auth/my-orgs')
      .then(({ data }) => setMyOrgs(data?.organizations || []))
      .catch(() => { /* silent */ });
  }, []);
  const switchOrg = async (targetUserId) => {
    try {
      const { data } = await api.post('/auth/switch-org', { user_id: targetUserId });
      if (data.session_token) setToken(data.session_token);
      window.location.href = data.redirect_path || '/app';
    } catch (e) { console.warn('[switch-org]', e); }
  };
  const displayOrg = org?.org_name || user?.org_name || user?.org || 'IFAAP';
  const displayEmail = user?.email || '';
  const avatar = user?.picture || `https://ui-avatars.com/api/?name=${encodeURIComponent(displayName)}&background=7C5CFC&color=fff`;

  return (
    <>
    {impersonation && (
      <div data-testid="impersonation-banner" className="bg-[#4F46E5] text-white px-6 py-2 flex items-center gap-3 text-[12.5px] sticky top-0 z-30">
        <Crown className="w-4 h-4 shrink-0" />
        <div className="flex-1">
          <span className="font-semibold">Mode Super Admin :</span> vous êtes connecté en tant que <span className="font-semibold">{impersonation.target_email}</span> · tenant <span className="font-semibold">{impersonation.tenant_name}</span>
        </div>
        <button onClick={stopImpersonation} data-testid="stop-impersonation" className="h-7 px-3 rounded-md bg-white/15 hover:bg-white/25 text-[11.5px] font-semibold">
          Quitter l'impersonation
        </button>
      </div>
    )}
    <header className="h-16 bg-white border-b border-[#EFF0F6] px-6 flex items-center gap-4 sticky top-0 z-20">
      <div className="flex-1 flex items-center gap-4">
        {title && (
          <h1 className="hidden md:block text-[17px] font-semibold text-[#1A1B2E] whitespace-nowrap" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
            {title}
          </h1>
        )}
        <button
          onClick={() => setSearchOpen(true)}
          className="relative max-w-md w-full hidden md:flex items-center h-10 pl-10 pr-4 rounded-xl bg-[#F6F7FB] border border-transparent text-[13.5px] text-[#8E8FA5] hover:bg-[#F0F1F7] transition-colors"
        >
          <Search className="w-[16px] h-[16px] absolute left-3.5 top-1/2 -translate-y-1/2 text-[#8E8FA5]" />
          Rechercher apprenants, sessions, documents…
          <kbd className="hidden lg:inline-flex absolute right-3 top-1/2 -translate-y-1/2 text-[10.5px] font-semibold px-1.5 py-0.5 rounded-md border border-[#E5E7EB] bg-white text-[#8E8FA5]">⌘K</kbd>
        </button>
      </div>

      <div className="flex items-center gap-1.5">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="hidden md:inline-flex items-center gap-1.5 h-9 px-3.5 text-[13px] font-semibold rounded-lg brand-gradient text-white hover:opacity-95 transition-opacity shadow-sm">
              <Plus className="w-4 h-4" strokeWidth={2.5} />
              Nouveau
              <ChevronDown className="w-3.5 h-3.5" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-52 rounded-xl">
            <DropdownMenuLabel>Créer rapidement</DropdownMenuLabel>
            <DropdownMenuSeparator />
            {QUICK_CREATE.map((q) => (
              <DropdownMenuItem key={q.label} onClick={() => navigate(q.route)}>
                <q.icon className="w-4 h-4 mr-2 text-[#4F46E5]" /> {q.label}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>

        <button
          data-testid="topbar-help"
          onClick={() => navigate('/app/aide')}
          className="w-9 h-9 flex items-center justify-center rounded-lg text-[#6B6C85] hover:bg-[#F6F7FB] transition-colors"
          title="Aide & documentation"
        >
          <HelpCircle className="w-[18px] h-[18px]" />
        </button>

        <Popover open={bellOpen} onOpenChange={setBellOpen}>
          <PopoverTrigger asChild>
            <button
              data-testid="topbar-bell"
              className={`relative w-9 h-9 flex items-center justify-center rounded-lg text-[#6B6C85] hover:bg-[#F6F7FB] transition-colors ${pulseBadge ? 'bell-pulse' : ''}`}
            >
              <Bell className="w-[18px] h-[18px]" />
              {unread > 0 && (
                <>
                  {pulseBadge && (
                    <span className="absolute top-1.5 right-1.5 w-[18px] h-[18px] rounded-full bg-[#EF4444] opacity-75 animate-ping" />
                  )}
                  <span
                    data-testid="topbar-bell-badge"
                    className="absolute top-1.5 right-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-[#EF4444] text-white text-[10px] font-bold flex items-center justify-center border-2 border-white tabular-nums"
                  >
                    {unread > 99 ? '99+' : unread}
                  </span>
                </>
              )}
            </button>
          </PopoverTrigger>
          <PopoverContent align="end" className="w-[420px] p-0 rounded-xl">
            <div className="px-4 py-3 border-b border-[#EFF0F6] flex items-center justify-between">
              <div>
                <div className="font-semibold text-[14px]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Notifications</div>
                <div className="text-[11px] text-[#8E8FA5] mt-0.5 flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#14B8A6] animate-pulse" />
                  {unread > 0 ? `${unread} non lue${unread > 1 ? 's' : ''}` : 'Tout est à jour'}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  data-testid="notif-filter-toggle"
                  onClick={() => setUnreadOnly((v) => !v)}
                  className={`text-[11.5px] px-2 h-7 rounded-md font-semibold transition-colors ${unreadOnly ? 'bg-[#4F46E5] text-white' : 'bg-[#EEF2FF] text-[#4F46E5] hover:bg-[#EDE8FF]'}`}
                  title={unreadOnly ? 'Afficher tout' : 'Afficher uniquement les non lues'}
                >
                  {unreadOnly ? 'Non lues' : 'Toutes'}
                </button>
                {unread > 0 && (
                  <button data-testid="notif-mark-all" onClick={markAll} className="text-[12px] text-[#4F46E5] font-semibold hover:underline">
                    Tout lu
                  </button>
                )}
              </div>
            </div>
            <div className="max-h-[440px] overflow-y-auto">
              {(() => {
                const visible = unreadOnly ? notifications.filter((n) => n.unread) : notifications;
                if (visible.length === 0) {
                  return (
                    <div className="px-4 py-8 text-center text-[12.5px] text-[#8E8FA5] italic">
                      {unreadOnly ? 'Aucune notification non lue' : 'Aucune notification pour le moment'}
                    </div>
                  );
                }
                return visible.map((n, idx) => {
                  const when = n.created_at || n.at;
                  const meta = EVENT_META[n.event];
                  const md = n.metadata || {};
                  const amount = md.amount_ttc || md.amount_eur;
                  const amountStr = amount ? `${Number(amount).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €` : null;
                  const handleClick = () => {
                    if (n.id) markOne(n.id);
                    if (n.link) { navigate(n.link); }
                  };
                  return (
                    <button
                      key={n.id || idx}
                      data-testid={`notif-${n.id || idx}`}
                      onClick={handleClick}
                      className={`w-full text-left px-4 py-3 border-b border-[#F3F4F8] last:border-0 hover:bg-[#FAFAFE] transition-colors flex gap-3 ${n.unread ? 'bg-[#FAFAFE]' : ''}`}
                    >
                      {meta ? (
                        <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${meta.tint}`}>
                          <meta.Icon className="w-4 h-4" />
                        </div>
                      ) : (
                        <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${
                          n.type === 'success' ? 'bg-[#E6FBF7] text-[#0F766E]' :
                          n.type === 'warning' ? 'bg-[#FEF3C7] text-[#92400E]' :
                          n.type === 'error' ? 'bg-[#FEF2F2] text-[#B91C1C]' : 'bg-[#E0F2FE] text-[#0369A1]'
                        }`}>
                          <Bell className="w-4 h-4" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="text-[13px] font-semibold text-[#1A1B2E] flex items-center gap-2">
                          <span className="truncate flex-1">{n.title || n.message || 'Notification'}</span>
                          {n.unread && <span className="w-1.5 h-1.5 rounded-full bg-[#4F46E5] shrink-0" />}
                        </div>
                        {(n.description || n.text) && (
                          <div className="text-[12px] text-[#4A4B63] line-clamp-2 mt-0.5">{n.description || n.text}</div>
                        )}
                        <div className="text-[11px] text-[#8E8FA5] mt-1 flex items-center gap-2 flex-wrap">
                          <span>{formatRelative(when) || n.time || ''}</span>
                          {amountStr && <span className="px-1.5 py-0.5 rounded bg-[#E6FBF7] text-[#0F766E] font-bold tabular-nums">{amountStr}</span>}
                          {md.signatory && <span className="text-[#0F766E] font-semibold">✓ {md.signatory}</span>}
                          {md.paid_via === 'stripe' && <span className="px-1.5 py-0.5 rounded bg-[#EEF2FF] text-[#635BFF] font-semibold">Stripe</span>}
                          {n.link && <span className="ml-auto text-[#4F46E5] font-semibold">Ouvrir →</span>}
                        </div>
                      </div>
                    </button>
                  );
                });
              })()}
            </div>
          </PopoverContent>
        </Popover>

        <div className="h-6 w-px bg-[#EFF0F6] mx-1" />

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="flex items-center gap-2 h-10 pl-1 pr-2 rounded-lg hover:bg-[#F6F7FB] transition-colors">
              <img src={avatar} alt={displayName} className="w-8 h-8 rounded-lg object-cover" />
              <div className="hidden md:block text-left">
                <div className="text-[12.5px] font-semibold text-[#1A1B2E] leading-tight">{displayName}</div>
                <div className="text-[11px] text-[#8E8FA5] leading-tight">{displayOrg}</div>
              </div>
              <ChevronDown className="w-4 h-4 text-[#8E8FA5]" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-60 rounded-xl">
            <DropdownMenuLabel>
              <div className="text-[13px] font-semibold">{displayName}</div>
              <div className="text-[11.5px] text-[#8E8FA5] font-normal">{displayEmail}</div>
              {org && (
                <div className="mt-2 pt-2 border-t border-[#F3F4F8] flex items-center gap-2">
                  <div className="w-7 h-7 rounded-lg brand-gradient text-white font-bold text-[11px] flex items-center justify-center">
                    {(org.org_name || 'O')[0]}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-[12px] font-semibold text-[#1A1B2E] truncate">{org.org_name}</div>
                    <div className="text-[10.5px] text-[#8E8FA5]">Plan {org.plan} · {org.members} membre{org.members > 1 ? 's' : ''}</div>
                  </div>
                </div>
              )}
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            {myOrgs.length > 1 && (
              <>
                <DropdownMenuLabel className="text-[10.5px] uppercase tracking-wider text-[#8E8FA5] py-1">
                  Changer d'organisme
                </DropdownMenuLabel>
                {myOrgs.filter((o) => !o.current).map((o) => (
                  <DropdownMenuItem key={o.user_id} data-testid={`switch-org-${o.org_id}`} onClick={() => switchOrg(o.user_id)}>
                    <ArrowLeftRight className="w-4 h-4 mr-2 text-[#14B8A6] shrink-0" />
                    <span className="truncate flex-1">{o.org_name}</span>
                    <span className="ml-2 text-[10px] text-[#8E8FA5]">{o.role}</span>
                  </DropdownMenuItem>
                ))}
                <DropdownMenuSeparator />
              </>
            )}
            <DropdownMenuItem data-testid="menu-profil" onClick={() => navigate('/app/profil')}><User className="w-4 h-4 mr-2" /> Mon compte</DropdownMenuItem>
            <DropdownMenuItem onClick={() => navigate('/app/parametres')}><Settings className="w-4 h-4 mr-2" /> Paramètres</DropdownMenuItem>
            <DropdownMenuItem
              data-testid="menu-customer-portal"
              onClick={async () => {
                try {
                  const { data } = await api.post('/billing/customer-portal', { return_url: window.location.origin + '/app' });
                  window.location.href = data.url;
                } catch (e) {
                  const detail = e?.response?.data?.detail || 'Portail indisponible';
                  // Show a soft toast/alert; UpsellModal won't fire on 503 (only 402).
                  alert(typeof detail === 'string' ? detail : (detail.message || 'Portail indisponible'));
                }
              }}
            >
              <CreditCard className="w-4 h-4 mr-2" /> Gérer mon abonnement
            </DropdownMenuItem>
            {(() => {
              const r = (user?.role || '').toLowerCase().replace(' ', '_');
              return (r === 'super_admin' || r === 'superadmin') ? (
                <DropdownMenuItem
                  data-testid="menu-superadmin"
                  onClick={() => navigate('/superadmin')}
                  className="text-[#4F46E5] font-semibold"
                >
                  <Crown className="w-4 h-4 mr-2" /> Super Admin
                </DropdownMenuItem>
              ) : null;
            })()}
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={handleLogout} className="text-[#EF4444]"><LogOut className="w-4 h-4 mr-2" /> Déconnexion</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <GlobalSearch open={searchOpen} onOpenChange={setSearchOpen} />
    </header>
    </>
  );
}
